import { useRef, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { toast } from 'sonner'
import { useTranslation } from '@kostbar/i18n'
import { ArrowDown, ArrowUp, Trash2 } from 'lucide-react'
import type { BrandingAd } from '@/api/endpoints/kiosk-branding'
import { imagesApi } from '@/api/endpoints/images'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'

/**
 * Limits mirrored from the server (`KioskBrandingValidator`), which remains the source of truth.
 *
 * The minimum width is enforced *here* rather than only server-side for a concrete reason: the
 * server can only measure formats the JDK can decode, so WebP and AVIF get past it unmeasured. The
 * browser decodes every accepted format natively, which makes this the one place the rule can
 * actually be applied to every upload.
 */
const MAX_ADS = 10
const MIN_DURATION = 3
const MAX_DURATION = 60
const MIN_WIDTH_PX = 1280

export function AdPlaylistEditor({
  ads,
  onChange,
  disabled,
}: {
  ads: Array<BrandingAd>
  onChange: (ads: Array<BrandingAd>) => void
  disabled?: boolean
}) {
  const { t } = useTranslation('restaurants')
  const inputRef = useRef<HTMLInputElement>(null)
  const [checking, setChecking] = useState(false)

  const upload = useMutation({
    mutationFn: (file: File) => imagesApi.uploadAdminImage(file),
    onSuccess: ({ ref }) => {
      onChange([...ads, { media: ref, durationSeconds: 8, active: true }])
      toast.success(t('branding.ads.uploadSuccess'))
    },
    onError: () => toast.error(t('branding.ads.uploadError')),
  })

  async function handleFile(file: File) {
    setChecking(true)
    try {
      const width = await naturalWidth(file)
      if (width > 0 && width < MIN_WIDTH_PX) {
        toast.error(t('branding.ads.tooSmall', { min: MIN_WIDTH_PX, width }))
        return
      }
      upload.mutate(file)
    } finally {
      setChecking(false)
    }
  }

  function move(index: number, delta: number) {
    const target = index + delta
    if (target < 0 || target >= ads.length) return
    const next = [...ads]
    const [item] = next.splice(index, 1)
    next.splice(target, 0, item)
    onChange(next)
  }

  function patch(index: number, changes: Partial<BrandingAd>) {
    onChange(ads.map((ad, i) => (i === index ? { ...ad, ...changes } : ad)))
  }

  const busy = disabled || checking || upload.isPending

  return (
    <div className="space-y-3">
      <div className="space-y-2">
        {ads.length === 0 && (
          <p className="text-muted-foreground text-sm">
            {t('branding.ads.empty')}
          </p>
        )}
        {ads.map((ad, index) => (
          <div
            key={`${ad.media}-${index}`}
            className="flex items-center gap-3 rounded-md border p-2"
          >
            <img
              src={imagesApi.mediaPreviewUrl(ad.media)}
              alt=""
              className="h-12 w-20 rounded object-cover"
            />
            <div className="flex items-center gap-2">
              <Input
                type="number"
                min={MIN_DURATION}
                max={MAX_DURATION}
                value={ad.durationSeconds ?? 8}
                disabled={busy}
                onChange={(e) =>
                  patch(index, {
                    durationSeconds: Number(e.target.value) || undefined,
                  })
                }
                className="w-20"
                aria-label={t('branding.ads.duration')}
              />
              <span className="text-muted-foreground text-sm">
                {t('branding.ads.seconds')}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Switch
                checked={ad.active}
                disabled={busy}
                onCheckedChange={(active) => patch(index, { active })}
                aria-label={t('branding.ads.active')}
              />
              <span className="text-muted-foreground text-sm">
                {t('branding.ads.active')}
              </span>
            </div>
            <div className="ml-auto flex items-center gap-1">
              <Button
                type="button"
                variant="ghost"
                size="icon"
                disabled={busy || index === 0}
                onClick={() => move(index, -1)}
                aria-label={t('branding.ads.moveUp')}
              >
                <ArrowUp className="h-4 w-4" />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                disabled={busy || index === ads.length - 1}
                onClick={() => move(index, 1)}
                aria-label={t('branding.ads.moveDown')}
              >
                <ArrowDown className="h-4 w-4" />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                disabled={busy}
                onClick={() => onChange(ads.filter((_, i) => i !== index))}
                aria-label={t('branding.ads.remove')}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
        ))}
      </div>

      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/avif"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0]
          e.target.value = ''
          if (file) void handleFile(file)
        }}
      />
      <Button
        type="button"
        variant="outline"
        disabled={busy || ads.length >= MAX_ADS}
        onClick={() => inputRef.current?.click()}
      >
        {ads.length >= MAX_ADS
          ? t('branding.ads.limitReached', { max: MAX_ADS })
          : t('branding.ads.add')}
      </Button>
    </div>
  )
}

/**
 * Pixel width of a chosen file, or 0 when it cannot be read.
 *
 * 0 means "unknown", never "too small" — a file the browser cannot decode is left to the server
 * rather than refused here on a guess.
 */
async function naturalWidth(file: File): Promise<number> {
  const url = URL.createObjectURL(file)
  try {
    return await new Promise<number>((resolve) => {
      const img = new Image()
      img.onload = () => resolve(img.naturalWidth)
      img.onerror = () => resolve(0)
      img.src = url
    })
  } finally {
    URL.revokeObjectURL(url)
  }
}
